import {
	Tile,
	Grid,
	InputField,
	H2,
	InfoChip,
	Spacer,
	TextArea,
	H5,
} from '@components';
import { CHIP_TYPE, FREIGHT_MODE, ILane, PartialInputEvent } from '@types';
import { useRecoilValue } from 'recoil';
import { RateCardFormErrors } from '@state';
import { RateCardCharacteristics } from './Characteristics';
import { useClearRCSettingsError, useIsInRoute, useRateCardState } from '@hooks';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';
import styles from './RateCard.module.scss';
import { getFreightModeLabel, getFreightModeIcon } from '@utils';

type TRateCardHeaderProps = {
	shipperTagName?: string | null;
	isDisabled?: boolean;
	isLinkedRateCard?: boolean;
}

export const RateCardHeader = (props: TRateCardHeaderProps) => {
	const {
		shipperTagName,
		isDisabled = false,
		isLinkedRateCard = false
	} = props;
	const [rateCard, setRateCard] = useRateCardState();
	const carrierInRoute = useIsInRoute('carrier');
	const quoteInRoute = useIsInRoute('quote');
	const rcFormErrors = useRecoilValue(RateCardFormErrors);
	const clearRCSettingsError = useClearRCSettingsError();

	const updateField = (name: string, value: string | PartialInputEvent) => {
		let newVal;
		if (typeof value === 'string') {
			newVal = value;
		} else {
			newVal = value.target.value;
		}
		const update = {
			...rateCard,
			[name]: newVal
		}
		setRateCard(update as ILane);

		// @ts-ignore
		clearRCSettingsError(`${name === 'nick_name' && 'description.'}${name}`)
	}

	const hasError = (keys: string | string[]) => {
		// return rcFormErrors?.[key]?._errors?.[0] || '';
		if (!Array.isArray(keys)) {
			// @ts-ignore
			return rcFormErrors?.[keys]?._errors?.[0] || '';
		}

		let errorObject: any = rcFormErrors;
		for (const key of keys) {
			if (!errorObject?.[key]) return '';
			errorObject = errorObject[key];
		}

		return errorObject?._errors?.[0] || '';
	}

	if (!rateCard) return
	return (
		<>
			<Spacer size={8} />
			{
				quoteInRoute &&
				rateCard.external_id && <H5>EDIT RATE CARD</H5>
			}
			{
				!isLinkedRateCard && <Grid columns={2} columnTemplate='1fr auto'>
					<div className={styles.locations}>
						<H2>
							{rateCard?.origin_location?.text}
						</H2>
						<ArrowCircleRightOutlinedIcon color='warning' />
						<H2>
							{rateCard?.destination_location?.text}
						</H2>
					</div>
					<div className={styles.row}>
						{
							shipperTagName &&
							<InfoChip
								text={shipperTagName}
								showIcon={false}
							/>
						}
						<InfoChip
							centerText
							collapse
							CustomIcon={getFreightModeIcon(rateCard?.freight_mode as FREIGHT_MODE || FREIGHT_MODE.ROAD)}
							text={getFreightModeLabel(rateCard?.freight_mode as FREIGHT_MODE|| FREIGHT_MODE.ROAD)}
						/>
					</div>
				</Grid>
			}
			{
				quoteInRoute && rateCard.external_id && <>
					<Spacer size={8} />
					<InfoChip
						stretch
						type={CHIP_TYPE.WARN}
						text={'By responding to this quote, you are editing an existing rate card'}
					/>
				</>
			}
			<Spacer size={20} />
			<Tile stretch>
				{
					isLinkedRateCard && <>
						<H2>Linked Details</H2>
						<Spacer size={20} />
					</>
				}
				{
					carrierInRoute && <Grid columns={2} minWidth={'60px'}>
						<InputField
							name={'nick_name'}
							label={'Nickname'}
							value={rateCard?.nick_name ?? ''}
							onChange={(e) => updateField(e.target.name, e.target.value)}
							placeholder={'Enter a name for your Rate Card'}
							required
							hasError={!!hasError(['description', 'nick_name'])}
							errorMessage={hasError(['description', 'nick_name'])}
							disabled={isDisabled}
						/>
						<TextArea
							name={'description'}
							label={'Description'}
							placeholder={'Enter a description'}
							value={rateCard?.description ?? ''}
							onChange={(e) => updateField(e.target.name, e.target.value)}
							keepErrorSpace={false}
							textAreaHeight='120px'
							disabled={isDisabled}
						/>
						<Grid.Item>
							<RateCardCharacteristics isDisabled={isDisabled} />
						</Grid.Item>
					</Grid>
				}
				{
					!carrierInRoute && <Grid columns={3} minWidth={'60px'}>
						<Grid.Item>
							<InputField
								name={'nick_name'}
								label={'Nickname'}
								value={rateCard?.nick_name ?? ''}
								onChange={(e) => updateField(e.target.name, e.target.value)}
								placeholder={'Enter a name for your Rate Card'}
								required
								hasError={!!hasError(['description', 'nick_name'])}
								errorMessage={hasError(['description', 'nick_name'])}
								disabled={isDisabled}
							/>
							<TextArea
								name={'description'}
								label={'Description'}
								placeholder={'Enter a description'}
								value={rateCard?.description ?? ''}
								onChange={(e) => updateField(e.target.name, e.target.value)}
								keepErrorSpace={false}
								textAreaHeight={'120px'}
								disabled={isDisabled}
							/>
						</Grid.Item>
						<Grid.Item>
							<RateCardCharacteristics isDisabled={isDisabled} />
						</Grid.Item>
					</Grid>
				}
			</Tile>
		</>
	);
}
